<template>
  <div class="RegularPurchaseList wide-container">
    <!-- 1. 顶部标题栏与新增分组入口 -->
    <div class="list-header-bar">
      <div class="header-left">
        <span class="header-accent-bar"></span>
        <h1 class="header-title">常购清单</h1>
        <span class="header-desc">按部门或高频周期分组管理，极速批量复购下单</span>
      </div>
      <div class="header-right">
        <button class="add-group-btn" @click="addGroup" type="button">
          <i class="el-icon-folder-add"></i>
          <span>新增清单分组</span>
        </button>
      </div>
    </div>

    <!-- 2. 分组列表区域 -->
    <div class="groups-container" v-if="regularPurchaseList.length > 0">
      <div
        v-for="(order, idx) in regularPurchaseList"
        :key="'group-' + (order.id || idx)"
        class="group-card"
      >
        <!-- 分组卡片头部 -->
        <div class="group-header">
          <div class="group-info">
            <div class="group-check-wrap">
              <el-checkbox
                :indeterminate="isGroupIndeterminate(order)"
                :value="isGroupAllChecked(order)"
                @change="(val) => handleGroupCheckAll(order, val)"
              ></el-checkbox>
            </div>
            <i class="el-icon-folder-opened group-icon"></i>

            <!-- 可编辑状态：输入框，失焦或回车后自动保存 -->
            <div class="group-title-edit-wrap" v-if="editingGroupId === order.id" @click.stop>
              <input
                ref="titleInput"
                type="text"
                class="group-title-input"
                v-model="editingGroupName"
                placeholder="请输入分组名称"
                maxlength="30"
                @blur="saveGroupName(order)"
                @keyup.enter="$event.target.blur()"
                @keyup.esc="cancelEditGroupName"
              />
            </div>

            <!-- 只读状态：点击后恢复可编辑状态 -->
            <div
              class="group-title-display-wrap"
              v-else
              @click.stop="startEditGroupName(order)"
              title="点击修改分组名称"
            >
              <span class="group-title-text">{{ order.izclrsecwsxu9yfg || '常规采购分组' }}</span>
              <i class="el-icon-edit-outline edit-icon"></i>
            </div>

            <span class="group-badge">{{ (order.vil3pydur6d8qauy && order.vil3pydur6d8qauy.length) || 0 }} 种物料</span>
          </div>

          <div class="group-actions">
            <button class="del-group-btn" @click="delGroup(order)" type="button">
              <i class="el-icon-delete"></i>
              <span>删除该分组</span>
            </button>
          </div>
        </div>

        <!-- 分组内部商品表格 -->
        <div class="group-table-wrap" v-if="order.vil3pydur6d8qauy && order.vil3pydur6d8qauy.length">
          <!-- 表头 -->
          <div class="table-header-grid">
            <div class="col-select">选择</div>
            <div class="col-info">商品信息与规格</div>
            <div class="col-price">协议单价</div>
            <div class="col-unit">计量单位</div>
            <div class="col-num">采购数量</div>
            <div class="col-action">操作</div>
          </div>

          <!-- 商品行 -->
          <div class="goods-row-list">
            <div
              v-for="(item, index) in order.vil3pydur6d8qauy"
              :key="'item-' + (item.id || index)"
              :class="['goods-row', { 'is-checked': item.checked }]"
            >
              <!-- 勾选框 -->
              <div class="col-select">
                <el-checkbox v-model="item.checked"></el-checkbox>
              </div>

              <!-- 商品信息 -->
              <div class="col-info">
                <div class="goods-thumb-box" @click="goToDetail(item)">
                  <img
                    :src="getGoodsImage(item)"
                    alt="商品图片"
                    class="thumb-img"
                  />
                </div>
                <div class="goods-meta-info">
                  <h3 class="goods-title" @click="goToDetail(item)" :title="getGoodsName(item)">
                    {{ getGoodsName(item) }}
                  </h3>
                  <div class="goods-sku-row" v-if="getSku(item)">
                    <span class="sku-tag">SKU: {{ getSku(item) }}</span>
                    <span class="sku-hint">周期常购规范物料</span>
                  </div>
                </div>
              </div>

              <!-- 协议单价 -->
              <div class="col-price">
                <div class="price-box tabular-nums">
                  <span class="currency">¥</span>
                  <span class="amount">{{ formatPrice(item.t9rtdu9o7iztvayy) }}</span>
                </div>
              </div>

              <!-- 计量单位 -->
              <div class="col-unit">
                <span class="unit-text">{{ item.kq1qbbk7dulmlghs || '件' }}</span>
              </div>

              <!-- 采购数量 (保留原有 NumberInput 步进器) -->
              <div class="col-num">
                <div class="stepper-box">
                  <NumberInput
                    @changeHandle="numberChange(item, order.id)"
                    :initNum="item.mcuilzaudpe3shlc"
                    v-model="item.mcuilzaudpe3shlc"
                    :min="1"
                    :max="99"
                  />
                </div>
              </div>

              <!-- 操作 -->
              <div class="col-action">
                <button
                  class="del-btn"
                  @click="deleteOrder(item.id)"
                  type="button"
                  title="从分组移除"
                >
                  <i class="el-icon-delete del-icon"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分组内无商品 -->
        <div class="group-empty-body" v-else>
          <i class="el-icon-document empty-sub-icon"></i>
          <span>该分组暂无物料，可在浏览商品时点击“加入常购”保存至此分组</span>
        </div>
      </div>
    </div>

    <!-- 3. 底部常驻结算栏 -->
    <div class="list-settle-bar" v-if="totalItemsCount > 0">
      <div class="settle-left">
        <el-checkbox
          :indeterminate="isGlobalIndeterminate"
          :value="isGlobalAllChecked"
          @change="handleGlobalCheckAll"
        >
          <span class="settle-select-text">
            全选 (已选 <strong class="highlight">{{ checkedCount }}</strong> 项共 <strong class="highlight">{{ checkedQuantity }}</strong> 件)
          </span>
        </el-checkbox>
      </div>

      <div class="settle-right">
        <div class="summary-box">
          <div class="price-row">
            <span class="summary-label">应付预估总额:</span>
            <div class="total-price tabular-nums">
              <span class="currency">¥</span>
              <span class="amount">{{ totalAmount }}</span>
            </div>
          </div>
          <div class="summary-hint">框架协议底价保真 · 支持直接并单对公结算</div>
        </div>

        <button
          class="checkout-btn"
          @click="settleAccounts"
          type="button"
          :disabled="checkedCount === 0"
        >
          <span>立即下单</span>
          <i class="el-icon-right"></i>
        </button>
      </div>
    </div>

    <!-- 4. 全局空状态 -->
    <div class="empty-list-card" v-else-if="regularPurchaseList.length === 0">
      <div class="empty-icon-wrap">
        <img src="../../assets/img/cart.svg" alt="常购清单为空" class="empty-img" />
      </div>
      <h3 class="empty-title">暂未建立常购清单分组</h3>
      <p class="empty-desc">创建“后勤清洁”、“打印耗材”等分组，让企业高频集采更快捷</p>
      <button class="empty-action-btn" @click="addGroup" type="button">
        <i class="el-icon-folder-add"></i>
        <span>创建第一个清单分组</span>
      </button>
    </div>
  </div>
</template>

<script>
import NumberInput from "../../components/NumberInput";
import {
  online,
  delOnline,
  updateOnline,
  children,
  postOnline,
} from "@/api/platform";

export default {
  name: "RegularPurchaseList",
  components: {
    NumberInput,
  },
  data () {
    return {
      form_head_uuid: "fde4a97f88444d9599a9bbf4778a2767",
      regularPurchaseList: [],
      editingGroupId: null,
      editingGroupName: "",
      originalGroupName: "",
    };
  },
  computed: {
    // 所有分组下商品拍平
    allGoodsItems () {
      const items = [];
      this.regularPurchaseList.forEach((group) => {
        if (Array.isArray(group.vil3pydur6d8qauy)) {
          group.vil3pydur6d8qauy.forEach((item) => items.push(item));
        }
      });
      return items;
    },
    totalItemsCount () {
      return this.allGoodsItems.length;
    },
    checkedItems () {
      return this.allGoodsItems.filter((item) => item.checked);
    },
    checkedCount () {
      return this.checkedItems.length;
    },
    checkedQuantity () {
      return this.checkedItems.reduce((acc, item) => {
        const qty = Number(item.mcuilzaudpe3shlc) || 0;
        return acc + qty;
      }, 0);
    },
    totalAmount () {
      let amount = 0;
      this.checkedItems.forEach((item) => {
        const price = Number(item.t9rtdu9o7iztvayy) || 0;
        const qty = Number(item.mcuilzaudpe3shlc) || 0;
        amount += price * qty;
      });
      return amount.toFixed(2);
    },
    isGlobalAllChecked () {
      return this.totalItemsCount > 0 && this.checkedCount === this.totalItemsCount;
    },
    isGlobalIndeterminate () {
      return this.checkedCount > 0 && this.checkedCount < this.totalItemsCount;
    },
  },
  mounted () {
    this.getRegularPurchaseList();
  },
  methods: {
    // 开启分组名称编辑
    startEditGroupName (order) {
      this.editingGroupId = order.id;
      this.editingGroupName = order.izclrsecwsxu9yfg || '';
      this.originalGroupName = order.izclrsecwsxu9yfg || '';
      this.$nextTick(() => {
        let inputEl = this.$refs.titleInput;
        if (Array.isArray(inputEl)) {
          inputEl = inputEl[0];
        }
        if (inputEl && inputEl.focus) {
          inputEl.focus();
          if (inputEl.select) {
            inputEl.select();
          }
        }
      });
    },
    // 保存分组名称（失焦或回车触发）
    async saveGroupName (order) {
      if (this.editingGroupId !== order.id) return;
      const trimmed = (this.editingGroupName || '').trim();
      if (!trimmed) {
        this.$message.warning('分组名称不能为空');
        this.editingGroupId = null;
        this.editingGroupName = '';
        return;
      }
      if (trimmed === (this.originalGroupName || '').trim()) {
        this.editingGroupId = null;
        return;
      }
      const prev = order.izclrsecwsxu9yfg;
      order.izclrsecwsxu9yfg = trimmed;
      this.editingGroupId = null;
      try {
        await updateOnline({
          form_head_uuid: this.form_head_uuid,
          id: order.id,
          izclrsecwsxu9yfg: trimmed,
        });
        this.$message.success('分组名称修改成功');
      } catch (err) {
        order.izclrsecwsxu9yfg = prev;
        this.$message.error('分组名称保存失败，请稍后重试');
      }
    },
    // 取消编辑
    cancelEditGroupName () {
      this.editingGroupId = null;
      this.editingGroupName = '';
    },
    // 新增分组
    addGroup () {
      this.$prompt('请输入常购清单分组名称（如：行政前台日常、二期车间劳保）：', '新增清单分组', {
        confirmButtonText: '立即创建',
        cancelButtonText: '取消',
        inputPattern: /\S+/,
        inputErrorMessage: '分组名称不能为空',
      }).then(async ({ value }) => {
        if (value && value.trim()) {
          await postOnline({
            form_head_uuid: this.form_head_uuid,
            izclrsecwsxu9yfg: value.trim(),
          });
          this.$message.success('分组创建成功');
          this.getRegularPurchaseList();
        }
      }).catch(() => {});
    },
    // 删除分组
    delGroup ({ id, izclrsecwsxu9yfg }) {
      const name = izclrsecwsxu9yfg || '该分组';
      this.$confirm(`确认删除清单分组【${name}】吗？删除后组内清单物料将一并移除。`, '删除分组提示', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        await delOnline({
          form_head_uuid: this.form_head_uuid,
          ids: [id],
        });
        this.$message.success('已删除该分组');
        this.getRegularPurchaseList();
      }).catch(() => {});
    },
    // 获取常购清单分组及子商品
    async getRegularPurchaseList () {
      const { form_head_uuid } = this;
      const { result } = await online({
        form_head_uuid,
        page: 1,
        limit: 1000,
        keyword: "",
        select_option: "",
      });
      if (Array.isArray(result) && result.length) {
        const list = await Promise.all(
          result.map(async (el) => ({
            ...el,
            vil3pydur6d8qauy: await this.fetchChildren(el.id),
          }))
        );
        this.regularPurchaseList = list;
      } else {
        this.regularPurchaseList = [];
      }
    },
    // 获取子表数据
    async fetchChildren (record_id) {
      const { form_head_uuid } = this;
      const { result } = await children({
        page: 1,
        limit: 100,
        field_uuid: "2190d51416b84722902efd1a5ee39dca",
        form_head_uuid,
        record_id,
      });
      if (Array.isArray(result)) {
        return result.map((el) => ({ ...el, checked: false }));
      }
      return [];
    },
    // 单组全选判断
    isGroupAllChecked (order) {
      if (!order || !order.vil3pydur6d8qauy || !order.vil3pydur6d8qauy.length) return false;
      return order.vil3pydur6d8qauy.every((item) => item.checked);
    },
    isGroupIndeterminate (order) {
      if (!order || !order.vil3pydur6d8qauy || !order.vil3pydur6d8qauy.length) return false;
      const checkedNum = order.vil3pydur6d8qauy.filter((item) => item.checked).length;
      return checkedNum > 0 && checkedNum < order.vil3pydur6d8qauy.length;
    },
    // 单组全选切换
    handleGroupCheckAll (order, checked) {
      if (order && order.vil3pydur6d8qauy) {
        order.vil3pydur6d8qauy.forEach((item) => {
          item.checked = checked;
        });
      }
    },
    // 全局全选切换
    handleGlobalCheckAll (checked) {
      this.allGoodsItems.forEach((item) => {
        item.checked = checked;
      });
    },
    // 数量调整
    async numberChange (item, id) {
      const obj = JSON.parse(JSON.stringify(item));
      delete obj.checked;
      if (Array.isArray(obj.mnkt8dkjryhvdej3)) {
        obj.mnkt8dkjryhvdej3 = obj.mnkt8dkjryhvdej3.map((el) => el.id);
      }
      if (Array.isArray(obj.whfmbyuc1i2pavi4)) {
        obj.whfmbyuc1i2pavi4 = obj.whfmbyuc1i2pavi4.map((el) => el.id);
      }
      const { form_head_uuid } = this;
      await updateOnline({
        id,
        form_head_uuid,
        vil3pydur6d8qauy: [obj],
      });
    },
    // 从常购清单删除单项商品
    async deleteOrder (orderId) {
      this.$confirm('确认从该常购清单分组中移除此商品吗？', '移除物料', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        await delOnline({
          form_head_uuid: 'd833435e05c511ef80bc0242321230b4',
          ids: [orderId],
        });
        this.$message.success('已移除该物料');
        this.getRegularPurchaseList();
      }).catch(() => {});
    },
    // 下单结算
    async settleAccounts () {
      const commodityList = this.checkedItems;
      if (commodityList.length === 0) {
        return this.$notify.error({
          title: '提示',
          message: '您还没有选择需要下单的商品',
        });
      }
      this.$store.commit('setData', {
        commodityList,
      });
      this.$router.push('/mall/createOrder?form=regularPurchaseList');
    },
    // 路由跳转
    navTo (route) {
      this.$router.push(route);
    },
    goToDetail (item) {
      if (item && item.whfmbyuc1i2pavi4 && item.whfmbyuc1i2pavi4.length > 0) {
        this.navTo('/mall/goods/' + item.whfmbyuc1i2pavi4[0].id);
      }
    },
    // 字段提取辅助
    getGoodsName (item) {
      if (!item) return '';
      if (item.whfmbyuc1i2pavi4 && item.whfmbyuc1i2pavi4.length > 0) {
        return item.whfmbyuc1i2pavi4[0].value;
      }
      return item.goodsName || '协议物料';
    },
    getGoodsImage (item) {
      if (!item) return '';
      if (item.mnkt8dkjryhvdej3 && item.mnkt8dkjryhvdej3.length > 0) {
        return item.mnkt8dkjryhvdej3[0].url;
      }
      return '';
    },
    getSku (item) {
      if (!item) return '';
      return item.p21recpjamiavinh || item.sku || item.goodsNo || item.levmoblohezp7y50 || '';
    },
    formatPrice (val) {
      const num = Number(val);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2);
    },
  },
};
</script>

<style scoped lang="less">
@import '../../assets/css/var.less';

.RegularPurchaseList {
  padding: 24px 0 60px;

  // 1. 顶部标题栏
  .list-header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    .header-left {
      display: flex;
      align-items: baseline;
      gap: 12px;

      .header-accent-bar {
        width: 4px;
        height: 18px;
        background-color: @brandPrimary;
        border-radius: 2px;
        align-self: center;
      }

      .header-title {
        font-size: 18px;
        font-weight: 700;
        color: @brandTextDark;
        margin: 0;
        letter-spacing: -0.01em;
      }

      .header-desc {
        font-size: 13px;
        color: @brandTextLight;
      }
    }

    .header-right {
      .add-group-btn {
        height: 36px;
        padding: 0 16px;
        background-color: @brandLight;
        color: @brandPrimary;
        border: 1px solid rgba(0, 60, 144, 0.2);
        border-radius: 6px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s ease;

        &:hover {
          background-color: @brandPrimary;
          color: #ffffff;
          border-color: @brandPrimary;
          box-shadow: 0 2px 6px rgba(0, 60, 144, 0.2);
        }
      }
    }
  }

  // 2. 分组卡片容器
  .groups-container {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .group-card {
      background: #ffffff;
      border-radius: 12px;
      border: 1px solid @brandBorder;
      box-shadow: 0 2px 8px rgba(0, 40, 100, 0.03);
      overflow: hidden;

      // 分组卡片头部
      .group-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 20px;
        background-color: #f8fafc;
        border-bottom: 1px solid @brandBorder;

        .group-info {
          display: flex;
          align-items: center;
          gap: 10px;

          .group-check-wrap {
            margin-right: 2px;
          }

          .group-icon {
            font-size: 16px;
            color: @brandContainer;
          }

          .group-title-edit-wrap {
            display: inline-flex;
            align-items: center;

            .group-title-input {
              height: 28px;
              padding: 0 8px;
              font-size: 14px;
              font-weight: 700;
              color: @brandTextDark;
              background-color: #ffffff;
              border: 1px solid @brandPrimary;
              border-radius: 4px;
              outline: none;
              box-shadow: 0 0 0 2px rgba(0, 60, 144, 0.15);
              transition: all 0.2s ease;

              &:focus {
                border-color: @brandPrimary;
                box-shadow: 0 0 0 3px rgba(0, 60, 144, 0.25);
              }
            }
          }

          .group-title-display-wrap {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 3px 8px;
            border-radius: 4px;
            cursor: pointer;
            border: 1px solid transparent;
            user-select: none;
            transition: all 0.2s ease;

            .group-title-text {
              font-size: 14px;
              font-weight: 700;
              color: @brandTextDark;
              transition: color 0.2s ease;
            }

            .edit-icon {
              font-size: 14px;
              color: #94a3b8;
              opacity: 0;
              transition: all 0.2s ease;
            }

            &:hover {
              background-color: rgba(0, 60, 144, 0.06);
              border-color: rgba(0, 60, 144, 0.2);

              .group-title-text {
                color: @brandPrimary;
              }

              .edit-icon {
                opacity: 1;
                color: @brandPrimary;
              }
            }
          }

          .group-badge {
            background-color: #e2e8f0;
            color: #475569;
            font-size: 11px;
            font-weight: 500;
            padding: 1px 8px;
            border-radius: 10px;
          }
        }

        .group-actions {
          .del-group-btn {
            border: none;
            background: transparent;
            color: #94a3b8;
            font-size: 12px;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            cursor: pointer;
            padding: 4px 8px;
            border-radius: 4px;
            transition: all 0.15s ease;

            &:hover {
              color: #ef4444;
              background-color: #fee2e2;
            }
          }
        }
      }

      // 栅格列基准
      .col-select {
        width: 70px;
        padding-left: 20px;
        flex-shrink: 0;
      }
      .col-info {
        flex: 1;
        min-width: 0;
        padding-right: 20px;
      }
      .col-price {
        width: 140px;
        text-align: center;
        flex-shrink: 0;
      }
      .col-unit {
        width: 90px;
        text-align: center;
        flex-shrink: 0;
      }
      .col-num {
        width: 160px;
        text-align: center;
        flex-shrink: 0;
      }
      .col-action {
        width: 80px;
        text-align: center;
        flex-shrink: 0;
      }

      // 表头
      .table-header-grid {
        display: flex;
        align-items: center;
        height: 40px;
        background-color: #fafbfc;
        border-bottom: 1px solid #f1f5f9;
        font-size: 12px;
        font-weight: 600;
        color: @brandTextMuted;
      }

      // 商品列表
      .goods-row-list {
        .goods-row {
          display: flex;
          align-items: center;
          padding: 16px 0;
          border-bottom: 1px solid #f1f5f9;
          transition: background-color 0.15s ease;

          &:last-child {
            border-bottom: none;
          }

          &:hover {
            background-color: #fafcff;
          }

          &.is-checked {
            background-color: #f6f9fe;
          }

          .col-info {
            display: flex;
            align-items: center;
            gap: 14px;

            .goods-thumb-box {
              width: 52px;
              height: 52px;
              border-radius: 8px;
              border: 1px solid @brandBorderLight;
              background-color: #ffffff;
              padding: 4px;
              box-sizing: border-box;
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: border-color 0.15s ease;

              &:hover {
                border-color: @brandContainer;
              }

              .thumb-img {
                width: 100%;
                height: 100%;
                object-contain: contain;
              }
            }

            .goods-meta-info {
              flex: 1;
              min-width: 0;

              .goods-title {
                font-size: 13px;
                font-weight: 600;
                color: @brandTextDark;
                margin: 0 0 4px;
                line-height: 1.4;
                cursor: pointer;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                transition: color 0.15s ease;

                &:hover {
                  color: @brandContainer;
                }
              }

              .goods-sku-row {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 11px;
                color: @brandTextLight;

                .sku-tag {
                  background-color: #f1f5f9;
                  padding: 1px 6px;
                  border-radius: 4px;
                }

                .sku-hint {
                  color: #94a3b8;
                }
              }
            }
          }

          .col-price {
            .price-box {
              font-size: 15px;
              font-weight: 700;
              color: @brandTextDark;

              .currency {
                font-size: 12px;
                font-weight: 600;
                margin-right: 2px;
                color: @brandPrimary;
              }

              .amount {
                color: @brandPrimary;
              }
            }
          }

          .col-unit {
            font-size: 13px;
            color: @brandTextMuted;
          }

          .col-num {
            display: flex;
            justify-content: center;

            .stepper-box {
              display: inline-block;
            }
          }

          .col-action {
            .del-btn {
              border: none;
              background: transparent;
              width: 32px;
              height: 32px;
              border-radius: 6px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              color: #94a3b8;
              cursor: pointer;
              transition: all 0.15s ease;

              .del-icon {
                font-size: 16px;
              }

              &:hover {
                color: #ef4444;
                background-color: #fee2e2;
              }
            }
          }
        }
      }

      .group-empty-body {
        padding: 32px 20px;
        text-align: center;
        font-size: 13px;
        color: @brandTextLight;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        .empty-sub-icon {
          font-size: 18px;
          color: #cbd5e1;
        }
      }
    }
  }

  // 3. 底部结算常驻操作栏
  .list-settle-bar {
    margin-top: 20px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid @brandBorder;
    box-shadow: 0 4px 14px rgba(0, 40, 100, 0.05);
    padding: 14px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .settle-left {
      display: flex;
      align-items: center;
      gap: 16px;

      .settle-select-text {
        font-size: 13px;
        color: @brandTextBase;

        .highlight {
          color: @brandPrimary;
          font-weight: 700;
          margin: 0 2px;
        }
      }
    }

    .settle-right {
      display: flex;
      align-items: center;
      gap: 20px;

      .summary-box {
        text-align: right;

        .price-row {
          display: flex;
          align-items: baseline;
          justify-content: flex-end;
          gap: 6px;

          .summary-label {
            font-size: 13px;
            font-weight: 500;
            color: @brandTextMuted;
          }

          .total-price {
            font-size: 24px;
            font-weight: 800;
            color: @brandPrimary;
            letter-spacing: -0.02em;

            .currency {
              font-size: 16px;
              font-weight: 700;
              margin-right: 2px;
            }
          }
        }

        .summary-hint {
          font-size: 11px;
          color: #94a3b8;
          margin-top: 2px;
        }
      }

      .checkout-btn {
        height: 44px;
        padding: 0 32px;
        background-color: @brandPrimary;
        color: #ffffff;
        border: none;
        border-radius: 8px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        box-shadow: 0 2px 8px rgba(0, 60, 144, 0.25);
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
          background-color: @brandDark;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 60, 144, 0.35);
        }

        &:disabled {
          background-color: #cbd5e1;
          cursor: not-allowed;
          box-shadow: none;
        }
      }
    }
  }

  // 4. 全局空状态
  .empty-list-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid @brandBorder;
    padding: 80px 20px;
    text-align: center;

    .empty-icon-wrap {
      width: 80px;
      height: 80px;
      margin: 0 auto 16px;
      border-radius: 50%;
      background-color: #f8fafc;
      display: flex;
      align-items: center;
      justify-content: center;

      .empty-img {
        width: 44px;
        height: 44px;
        opacity: 0.6;
      }
    }

    .empty-title {
      font-size: 16px;
      font-weight: 700;
      color: @brandTextDark;
      margin: 0 0 6px;
    }

    .empty-desc {
      font-size: 13px;
      color: @brandTextLight;
      margin: 0 0 20px;
    }

    .empty-action-btn {
      height: 38px;
      padding: 0 20px;
      background-color: @brandPrimary;
      color: #ffffff;
      border: none;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.15s ease;

      &:hover {
        background-color: @brandDark;
      }
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/pages/client/RegularPurchaseList.vue